import { shortFormat } from 'utils/dates';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { LinkQueryResult } from 'components/traceInspector/traceInspector.types';
import { count } from '@/components/traceInspector/traceInspector.utils';
import { cn } from '@/shadcn/lib/utils';
import { Badge } from '@/shadcn/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import { AvatarComponent } from '@/components/ui/avatar/avatar';

interface Props {
  isLast: boolean;
  isFirst: boolean;
  interval: string | null;
  group: LinkQueryResult[];
  firstAction: LinkQueryResult;
  lastAction: LinkQueryResult;
}

export const SegmentGroup = ({
  isLast,
  isFirst,
  interval,
  group,
  firstAction,
  lastAction
}: Props) => {
  const renderWhat = (): string => {
    const { action, actionKey, referencedBy, references } = firstAction;
    const what = action ? action.title : actionKey;
    const postfix =
      !!referencedBy.totalCount || !!references.totalCount ? '*' : '';
    return `${postfix}${what.slice(0, 35).trim()}${what.slice(35) && '…'}`;
  };

  const numberOfGroups = new Set(group.map(action => action.group.name)).size;

  const plageActionNumber = `${count(firstAction.height)} - ${count(
    lastAction.height
  )}`;

  return (
    <div
      className={cn(
        'group relative z-1 mb-4 flex w-[161px] appearance-none flex-col items-end justify-start border-none bg-transparent p-0 focus:outline-hidden',
        'before:bg-border before:absolute before:top-20 before:left-0 before:z-[-1] before:h-px before:w-20',
        'after:bg-border after:absolute after:top-20 after:right-0 after:z-[-1] after:h-px after:w-20',
        { 'w-[126px]': isFirst && isLast },
        { 'w-[126px] before:w-0': isFirst && !isLast }
      )}
      style={{ paddingTop: `14px` }}
    >
      <div className="w-[126px] opacity-100 transition-opacity duration-200 hover:opacity-100">
        <div className="relative top-[3px] py-[3px] text-center text-[9px] leading-4 font-normal">
          {plageActionNumber}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute top-0 right-0 z-1">
                  <Badge className="group-hover:border-primary-deep/20 px-2">
                    {group.length}
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{group.length} actions are grouped, click to expand</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="truncate text-center text-[11px] leading-4 font-bold">
          {renderWhat()}
        </div>
      </div>
      <div
        className="relative mb-[2px] flex"
        data-is-first={isFirst}
        data-is-last={isLast}
      >
        <div className="relative top-[2px] z-1 flex w-[35px] shrink-0 items-center justify-center">
          {interval && (
            <div className="bg-card text-muted-foreground max-w-[35px] shrink-0 truncate px-[2px] pb-[6px] text-[9px]">
              {interval}
            </div>
          )}
        </div>
        <div className="mx-auto grid h-[60px] grid-flow-col grid-cols-[42px_38px_42px] grid-rows-1 items-center gap-1.5">
          <div
            className={cn(
              'bg-card text-muted-foreground relative col-start-2 flex h-9 w-11 items-center justify-center px-1',
              isLast && 'text-foreground'
            )}
          >
            <DynamicIcon
              icon={(firstAction.action && firstAction.action.icon) || 'Doc'}
              size={35}
            />
          </div>
        </div>
      </div>
      <div className="w-[126px] opacity-60 transition-opacity duration-200 group-hover:opacity-100">
        <div className="text-center text-[10px]">
          {`${shortFormat(firstAction.createdAt)} - ${shortFormat(
            lastAction.createdAt
          )}`}
        </div>
        <div className="w-full truncate text-center text-[11px] font-bold">
          {/* {numberOfGroups} teams */}
          {numberOfGroups === 1
            ? (group?.[0]?.group?.name ?? '1 team')
            : `${numberOfGroups} teams`}
        </div>
        <div className="relative z-1 w-full shrink-0">
          <div className="bg-card mx-auto h-8 w-9">
            <AvatarComponent
              size={36}
              source={firstAction.group.avatar}
              name={firstAction.group.name}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
